import styled from 'styled-components';
import { theme } from '../theme';

// Form Container
export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

export const FormRow = styled.div<{ columns?: number }>`
  display: grid;
  grid-template-columns: repeat(${props => props.columns || 1}, 1fr);
  gap: ${theme.spacing[4]};
  
  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

// Labels
export const Label = styled.label<{ required?: boolean }>`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.text.primary};
  
  ${props => props.required && `
    &::after {
      content: ' *';
      color: ${theme.colors.error[500]};
    }
  `}
`;

export const HelperText = styled.span<{ error?: boolean }>`
  font-size: ${theme.typography.fontSize.xs};
  color: ${props => props.error ? theme.colors.error[600] : theme.colors.text.secondary};
  margin-top: ${theme.spacing[1]};
`;

// Base Input Styles
export const BaseInput = styled.input<{ hasError?: boolean }>`
  width: 100%;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${props => props.hasError ? theme.colors.error[300] : theme.colors.border.medium};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.base};
  font-family: ${theme.typography.fontFamily.primary};
  background-color: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  transition: all ${theme.transitions.fast};
  
  &::placeholder {
    color: ${theme.colors.text.tertiary};
  }
  
  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? theme.colors.error[500] : theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${props => props.hasError ? theme.colors.error[100] : theme.colors.primary[100]};
  }
  
  &:disabled {
    background-color: ${theme.colors.gray[100]};
    color: ${theme.colors.text.tertiary};
    cursor: not-allowed;
  }
`;

export const TextInput = styled(BaseInput)``;

export const PasswordInput = styled(BaseInput).attrs({ type: 'password' })``;

export const EmailInput = styled(BaseInput).attrs({ type: 'email' })``;

export const NumberInput = styled(BaseInput).attrs({ type: 'number' })``;

// Textarea
export const Textarea = styled.textarea<{ hasError?: boolean; resize?: boolean }>`
  width: 100%;
  min-height: 100px;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${props => props.hasError ? theme.colors.error[300] : theme.colors.border.medium};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.base};
  font-family: ${theme.typography.fontFamily.primary};
  background-color: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  transition: all ${theme.transitions.fast};
  resize: ${props => props.resize ? 'vertical' : 'none'};
  
  &::placeholder {
    color: ${theme.colors.text.tertiary};
  }
  
  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? theme.colors.error[500] : theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${props => props.hasError ? theme.colors.error[100] : theme.colors.primary[100]};
  }
  
  &:disabled {
    background-color: ${theme.colors.gray[100]};
    color: ${theme.colors.text.tertiary};
    cursor: not-allowed;
  }
`;

// Select
export const Select = styled.select<{ hasError?: boolean }>`
  width: 100%;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${props => props.hasError ? theme.colors.error[300] : theme.colors.border.medium};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.base};
  font-family: ${theme.typography.fontFamily.primary};
  background-color: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  transition: all ${theme.transitions.fast};
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? theme.colors.error[500] : theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${props => props.hasError ? theme.colors.error[100] : theme.colors.primary[100]};
  }
  
  &:disabled {
    background-color: ${theme.colors.gray[100]};
    color: ${theme.colors.text.tertiary};
    cursor: not-allowed;
  }
`;

// Checkbox and Radio
export const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

export const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  width: 16px;
  height: 16px;
  accent-color: ${theme.colors.primary[500]};
  cursor: pointer;
  
  &:disabled {
    cursor: not-allowed;
  }
`;

export const RadioContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

export const Radio = styled.input.attrs({ type: 'radio' })`
  width: 16px;
  height: 16px;
  accent-color: ${theme.colors.primary[500]};
  cursor: pointer;
  
  &:disabled {
    cursor: not-allowed;
  }
`;

export const CheckboxLabel = styled.label`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.primary};
  cursor: pointer;
  user-select: none;
`;

// Input Groups
export const InputGroup = styled.div`
  display: flex;
  width: 100%;
`;

export const InputGroupAddon = styled.div<{ position: 'left' | 'right' }>`
  display: flex;
  align-items: center;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background-color: ${theme.colors.gray[100]};
  border: 1px solid ${theme.colors.border.medium};
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.text.secondary};
  white-space: nowrap;
  
  ${props => props.position === 'left' && `
    border-right: none;
    border-radius: ${theme.borderRadius.md} 0 0 ${theme.borderRadius.md};
  `}
  
  ${props => props.position === 'right' && `
    border-left: none;
    border-radius: 0 ${theme.borderRadius.md} ${theme.borderRadius.md} 0;
  `}
`;

export const InputGroupInput = styled(BaseInput)<{ hasAddon?: 'left' | 'right' | 'both' }>`
  ${props => {
    if (props.hasAddon === 'left') {
      return `border-radius: 0 ${theme.borderRadius.md} ${theme.borderRadius.md} 0;`;
    }
    if (props.hasAddon === 'right') {
      return `border-radius: ${theme.borderRadius.md} 0 0 ${theme.borderRadius.md};`;
    }
    if (props.hasAddon === 'both') {
      return `border-radius: 0;`;
    }
    return '';
  }}
`;

// File Input
export const FileInput = styled.input.attrs({ type: 'file' })`
  width: 100%;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border.medium};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.base};
  font-family: ${theme.typography.fontFamily.primary};
  background-color: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[100]};
  }
  
  &:disabled {
    background-color: ${theme.colors.gray[100]};
    cursor: not-allowed;
  }
`;

// Search Input
export const SearchInput = styled(BaseInput)`
  padding-left: ${theme.spacing[10]};
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m21 21-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'%3E%3C/path%3E%3C/svg%3E");
  background-size: 16px 16px;
  background-position: ${theme.spacing[3]} center;
  background-repeat: no-repeat;
`;
